import { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import axiosInstance from '@/lib/axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Calendar,
  Ticket,
  Loader2,
  XCircle,
  Download,
  Share2,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface TicketDetails {
  id: string;
  status: string;
  name: string;
  email: string;
  phone: string | null;
  formData: Record<string, unknown>;
  createdAt: string;
  eventTitle: string;
  startDate: string;
  endDate: string;
  banner: string | null;
  ticketName: string;
  ticketType: string;
  price: number;
  userId: string;
}

interface MyTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  registrationId: string;
  eventId: string;
  onCancelSuccess: () => void;
  registrations?: Array<{ id: string; status: string }>;
  onNavigate?: (id: string) => void;
  onBackToList?: () => void;
}

export default function MyTicketDialog({
  open,
  onOpenChange,
  registrationId,
  eventId,
  onCancelSuccess,
  registrations = [],
  onNavigate,
  onBackToList,
}: MyTicketDialogProps) {
  const [ticketDetails, setTicketDetails] = useState<TicketDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentIndex = registrations.findIndex((r) => r.id === registrationId);
  const hasMultiple = registrations.length > 1;

  const handleShare = async () => {
    if (!ticketDetails) return;
    const text = `I'm attending ${ticketDetails.eventTitle}! Check it out.`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: ticketDetails.eventTitle,
          text: text,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(`${text} ${window.location.href}`);
        toast.success('Link copied to clipboard');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleDownload = () => {
    const svg = document.querySelector('.qr-code-container svg');
    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        const pngFile = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.download = `ticket-${registrationId}.png`;
        downloadLink.href = pngFile;
        downloadLink.click();
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    }
  };

  // Fetch ticket details when dialog opens
  const fetchTicketDetails = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        `/api/events/${eventId}/registrations/${registrationId}`
      );
      setTicketDetails(response.data.registration);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    } finally {
      setIsLoading(false);
    }
  }, [eventId, registrationId]);

  // Handle cancel registration
  const handleCancelRegistration = async () => {
    setIsCancelling(true);
    try {
      await axiosInstance.delete(`/api/events/${eventId}/registrations/${registrationId}`);
      onCancelSuccess();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel registration');
    } finally {
      setIsCancelling(false);
      setShowCancelDialog(false);
    }
  };

  // Fetch details when dialog opens
  useEffect(() => {
    if (open && registrationId && eventId) {
      fetchTicketDetails();
    } else if (!open) {
      setTicketDetails(null);
      setError(null);
    }
  }, [open, registrationId, eventId, fetchTicketDetails]);

  const getStatusDisplay = (status: string) => {
    const statusConfig: Record<string, { colorClass: string; label: string }> = {
      PENDING: { colorClass: 'bg-amber-500', label: 'Pending' },
      CONFIRMED: { colorClass: 'bg-emerald-500', label: 'Confirmed' },
      ATTENDED: { colorClass: 'bg-blue-500', label: 'Attended' },
      CANCELLED: { colorClass: 'bg-destructive', label: 'Cancelled' },
    };
    const config = statusConfig[status] || { colorClass: 'bg-muted-foreground', label: status };
    return (
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted/30 px-2 py-1 rounded-full border border-muted">
        <span className={`h-1.5 w-1.5 rounded-full ${config.colorClass}`} />
        {config.label}
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent closeOutside className="sm:max-w-[425px]">
          <DialogHeader className="shrink-0 space-y-1.5 p-4 pb-0 pr-12 text-left sm:p-6 sm:pb-0">
            <div className="flex items-center gap-2">
              {hasMultiple && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 -ml-2 text-muted-foreground"
                  onClick={() => {
                    if (onBackToList) onBackToList();
                    else onOpenChange(false);
                  }}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <DialogTitle>Ticket Details</DialogTitle>
              {ticketDetails && getStatusDisplay(ticketDetails.status)}
            </div>
            <DialogDescription className="sr-only">
              Your event ticket and registration details.
            </DialogDescription>

            {hasMultiple && (
              <div className="flex items-center justify-between w-full !mt-2 border-b pb-1">
                <span className="text-md text-muted-foreground font-medium">
                  {currentIndex + 1} of {registrations.length}
                </span>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={currentIndex <= 0}
                    onClick={() => onNavigate?.(registrations[currentIndex - 1].id)}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={currentIndex >= registrations.length - 1}
                    onClick={() => onNavigate?.(registrations[currentIndex + 1].id)}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {error && (
            <div className="text-center py-8 text-destructive">
              <p>{error}</p>
            </div>
          )}

          {ticketDetails && !isLoading && (
            <div className="space-y-4">
              {/* Event Info & Actions */}
              <div className="flex flex-col items-center bg-muted/10 p-2 rounded-lg border border-muted/20">
                <h3 className="font-bold text-3xl sm:text-4xl tracking-tight leading-tight text-center">
                  {ticketDetails.eventTitle}
                </h3>

                <div className="w-full flex items-end justify-between mt-4">
                  <div className="space-y-1.5">
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Calendar className="mr-1.5 h-3.5 w-3.5" />
                      {format(new Date(ticketDetails.startDate), 'PPP')}
                    </div>
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Ticket className="mr-1.5 h-3.5 w-3.5" />
                      {ticketDetails.ticketName}
                      {ticketDetails.price > 0 && ` - ₹${ticketDetails.price}`}
                    </div>
                  </div>
                  <div className="flex items-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 text-muted-foreground"
                      onClick={handleShare}
                      title="Share"
                    >
                      <Share2 className="h-7 w-7" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 text-muted-foreground"
                      onClick={handleDownload}
                      title="Download"
                    >
                      <Download className="h-7 w-7" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* QR Code - Only show for confirmed/attended tickets */}
              {(ticketDetails.status === 'CONFIRMED' || ticketDetails.status === 'ATTENDED') && (
                <div className="flex flex-col items-center space-y-3 bg-muted/20 py-2 rounded-xl border border-muted/50">
                  <div className="text-center">
                    <p className="text-xl font-bold text-foreground">{ticketDetails.name}</p>
                  </div>
                  <div className="bg-white p-3 rounded-xl border shadow-sm qr-code-container">
                    <QRCodeSVG
                      value={
                        ticketDetails.userId ? `M:${ticketDetails.userId}` : `V:${ticketDetails.id}`
                      }
                      size={200}
                      className="h-auto w-full max-w-[200px]"
                      level="H"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    Show this QR code at the event entrance
                  </p>
                </div>
              )}

              <Separator className="my-2" />

              {/* Registration Details */}
              <div className="space-y-1">
                <h4 className="font-medium text-sm">Registration Details</h4>
                <div className="grid grid-cols-[80px_1fr] sm:grid-cols-[100px_1fr] gap-x-2 gap-y-1 text-xs">
                  <div className="text-muted-foreground">Email:</div>
                  <div className="break-all">{ticketDetails.email}</div>
                  {ticketDetails.phone && (
                    <>
                      <div className="text-muted-foreground">Phone:</div>
                      <div className="break-all">{ticketDetails.phone}</div>
                    </>
                  )}
                  <div className="text-muted-foreground">Registered:</div>
                  <div className="truncate">{format(new Date(ticketDetails.createdAt), 'PPp')}</div>
                </div>
              </div>

              {/* Custom Form Data */}
              {ticketDetails.formData && Object.keys(ticketDetails.formData).length > 0 && (
                <>
                  <Separator className="my-2" />
                  <div className="space-y-1">
                    <h4 className="font-medium text-sm">Additional Information</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-x-2 gap-y-2 text-xs">
                      {Object.entries(ticketDetails.formData).map(([key, value]) => (
                        <div key={key} className="flex flex-col sm:contents gap-y-0.5">
                          <div className="text-muted-foreground capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                          </div>
                          <div className="break-words whitespace-pre-wrap">{String(value)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          </div>

          <DialogFooter className="mt-0 shrink-0 border-t p-4 sm:justify-between items-center sm:flex-row flex-col-reverse gap-2 sm:p-6">
            {ticketDetails &&
              ticketDetails.status !== 'CANCELLED' &&
              ticketDetails.status !== 'ATTENDED' && (
                <Button
                  variant="ghost"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive w-full sm:w-auto"
                  onClick={() => setShowCancelDialog(true)}
                  disabled={isCancelling}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel Registration
                </Button>
              )}
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Registration?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel your registration? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>No, keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelRegistration}
              disabled={isCancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isCancelling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Yes, cancel registration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
